import type { LocalMessage, ThreadSummary } from '@swchat/client-sdk';
import { useRef, useState } from 'react';
import { Avatar, Icon, IconButton } from '../design';
import { forwardedLabel, quoteText } from '../lib/display';
import { FileContent } from './FileContent';
import { timeFmt } from './format';
import { LazyCallLog, LazyLocationContent, LazyPollContent, LazyReactionPicker } from './lazy';
import { ReminderSuggestion } from './ReminderSuggestion';
import { RichText } from './RichText';

export interface BubbleProps {
  m: LocalMessage;
  me?: string;
  mine: boolean;
  direct: boolean;
  senderName: (userId: string) => string | undefined;
  /** Người gửi là bot (hiện nhãn "Bot" cạnh tên). */
  senderIsBot?: boolean;
  names: string[];
  /** undefined: không phải trả lời; null: tin gốc không có trên thiết bị. */
  quoted?: LocalMessage | null;
  mentioned: boolean;
  highlighted: boolean;
  /** Hội thoại đã rời: chỉ xem, ẩn các thao tác. */
  readOnly?: boolean;
  /** Tin liền trước cùng người gửi (gộp nhóm: ẩn avatar + tên). */
  continued?: boolean;
  seen?: string;
  /** W2.1: tin đang được ghim. */
  pinned?: boolean;
  /** W2.2: luồng của tin này (số phản hồi) — hiện "N phản hồi · Mở luồng". */
  thread?: ThreadSummary;
  onQuoteClick: () => void;
  /** undefined: không trả lời trích dẫn được (vd. tin trong khung luồng — trả lời bằng ô soạn của luồng). */
  onReply?: () => void;
  /** W2.2: mở luồng (trả lời trong luồng); undefined khi tin đã nằm trong khung luồng. */
  onOpenThread?: () => void;
  /** W2.1: ghim/bỏ ghim; undefined khi không có quyền (member nhóm). */
  onPin?: () => void;
  /** Đánh dấu / bỏ đánh dấu quan trọng — chỉ có với tin do chính mình gửi. */
  onImportant?: () => void;
  /** Bong bóng trong khung luồng: id phần tử riêng (tin gốc cũng có ở dòng chính — không trùng id, cuộn tới tin vẫn trỏ dòng chính). */
  inThread?: boolean;
  /** undefined: hội thoại bí mật — không cho chuyển tiếp. */
  onForward?: () => void;
  onReact: (emoji: string, remove: boolean) => void;
  onEdit: () => void;
  onDelete: () => void;
  /** Tin của mình gửi lỗi (vd. 429 giới hạn tần suất) → gửi lại. */
  onResend: () => void;
}

/** Bề rộng ước tính của thanh thao tác đầy đủ (tối đa 8 nút) — dưới mức này thì đặt xuống dưới bong bóng. */
const ACTIONS_WIDTH = 8 * 30 + 12;

/** Một tin nhắn: avatar + tên (nhóm), trích dẫn, nội dung, reaction, trạng thái, thanh thao tác khi rê chuột/focus. */
export function Bubble(props: BubbleProps) {
  const { m, mine } = props;
  const [picker, setPicker] = useState(false);
  const reactRef = useRef<HTMLButtonElement>(null);
  const rowRef = useRef<HTMLDivElement>(null);
  const bubbleRef = useRef<HTMLDivElement>(null);
  /**
   * Vị trí thanh thao tác: bên cạnh bong bóng khi còn đủ chỗ trống (không che tin nào), không thì ngay dưới bong bóng.
   * Đo lúc rê chuột/focus vào tin (chiều rộng bong bóng đổi theo nội dung và độ rộng khung chat).
   */
  const [actionsAt, setActionsAt] = useState<'side' | 'below'>('below');
  const measureActions = (fromFocus = false) => {
    // Đang rê chuột trên tin: KHÔNG đo lại khi nút nhận focus lúc bấm — thanh nhảy chỗ giữa nhấn và nhả chuột sẽ làm mất cú bấm.
    if (fromFocus && rowRef.current?.matches(':hover')) return;
    const row = rowRef.current?.getBoundingClientRect();
    const bubble = bubbleRef.current?.getBoundingClientRect();
    if (!row || !bubble) return;
    const room = mine ? bubble.left - row.left : row.right - bubble.right;
    setActionsAt(room >= ACTIONS_WIDTH + 12 ? 'side' : 'below');
  };
  // Tin file: trả lời / thả cảm xúc / xóa được; không sửa, không chuyển tiếp (giả định 28).
  const usable = !props.readOnly && !m.deleted && (m.type === 'text' || m.type === 'file' || m.type === 'poll' || m.type === 'location') && m.seq !== null;
  const isText = m.type === 'text';
  /** Luồng và ghim chỉ áp dụng cho tin văn bản/file. */
  const threadable = m.type === 'text' || m.type === 'file';
  const body = m.deleted ? (
    <i className="ws-muted">Tin nhắn đã bị xóa</i>
  ) : m.type === 'undecryptable' ? (
    <i className="ws-muted">Không giải mã được tin nhắn này trên thiết bị này</i>
  ) : m.type === 'file' && m.file ? (
    <FileContent m={m} noAccess={!!props.readOnly} />
  ) : m.type === 'poll' && m.poll ? (
    <LazyPollContent m={m} me={props.me} readOnly={!!props.readOnly} senderName={props.senderName} />
  ) : m.type === 'location' && m.location ? (
    <LazyLocationContent location={m.location} />
  ) : m.type === 'call' && m.call ? (
    <LazyCallLog call={m.call} text={m.text} />
  ) : (
    <span className="ws-text" data-testid="message-text">
      <RichText text={m.text ?? ''} entities={m.entities} names={props.names} />
    </span>
  );
  const forwarded = forwardedLabel(m, props.senderName);
  const quote = props.quoted === undefined ? undefined : quoteText(props.quoted, props.senderName);
  const reactions = m.deleted ? [] : Object.entries(m.reactions ?? {}).filter(([, users]) => users.length);
  const sender = props.senderName(m.sender_user_id) ?? '…';
  // Tên người gửi hiện ở mọi tin của người khác (cả chat 1-1 và tin liền nhau); tin của mình không cần.
  const showAuthor = !mine;
  return (
    <div
      ref={rowRef}
      onMouseEnter={() => measureActions()}
      onFocusCapture={() => measureActions(true)}
      data-actions={actionsAt}
      id={`${props.inThread ? 'thread-msg' : 'msg'}-${m.id}`}
      className={`ws-msg${mine ? ' is-mine' : ''}${props.continued ? ' is-continued' : ''}`}
      data-testid="message"
      data-msg-id={m.id}
    >
      {!mine && (props.continued ? <span className="ws-msg-gutter" /> : <Avatar name={sender} seed={m.sender_user_id} size="sm" />)}
      <div className="ws-msg-body">
        <div
          ref={bubbleRef}
          data-testid="bubble"
          className={[
            'ws-bubble',
            m.state === 'failed' ? 'failed' : '',
            props.mentioned && !m.deleted ? 'mentioned' : '',
            props.highlighted ? 'highlighted' : '',
            m.important && !m.deleted ? 'important' : '',
          ]
            .filter(Boolean)
            .join(' ')}
        >
          {showAuthor && (
            <div className="ws-sender">
              {sender}
              {props.senderIsBot && (
                <span className="ws-bot-tag" data-testid="bot-tag">
                  BOT
                </span>
              )}
            </div>
          )}
          {m.important && !m.deleted && (
            <div className="ws-important-label" data-testid="important-label">
              <Icon name="star" size={12} /> Tin quan trọng
            </div>
          )}
          {props.pinned && !m.deleted && (
            <div className="ws-pinned-label" data-testid="pinned-label">
              <Icon name="pin" size={12} /> Đã ghim
            </div>
          )}
          {forwarded && (
            <div className="ws-forwarded" data-testid="forwarded">
              {forwarded}
            </div>
          )}
          {props.quoted !== undefined && !m.deleted && (
            <button type="button" className="ws-quote" data-testid="quote" onClick={props.onQuoteClick} title="Tới tin gốc">
              {quote?.sender !== undefined ? (
                <>
                  <strong>{quote.sender}</strong>
                  <span>{quote.text}</span>
                </>
              ) : (
                <span className="ws-muted">{quote?.text}</span>
              )}
            </button>
          )}
          {body}
          {isText && usable && m.state !== 'failed' && m.text && <ReminderSuggestion text={m.text} sentAt={m.ts} />}
          {props.thread && props.thread.reply_count > 0 && props.onOpenThread && (
            <button type="button" className="ws-thread-summary" data-testid="thread-summary" onClick={props.onOpenThread}>
              <Icon name="chat" size={13} />
              {props.thread.reply_count} phản hồi · Mở luồng
            </button>
          )}
          <div className="ws-meta">
            {m.edited_at && !m.deleted && <span>đã sửa · </span>}
            {timeFmt.format(m.ts)}
            {mine && (
              <span>
                {' · '}
                <span data-testid="message-status">{m.state === 'pending' ? 'đang gửi' : m.state === 'failed' ? 'lỗi' : (props.seen ?? '✓')}</span>
              </span>
            )}
            {mine &&
              m.state === 'failed' &&
              !m.deleted &&
              !props.readOnly &&
              (m.type === 'text' || m.type === 'file' || m.type === 'poll' || m.type === 'location') && (
                <>
                  {' · '}
                  <button type="button" className="ws-link-btn ws-resend" data-testid="message-resend" onClick={props.onResend}>
                    Gửi lại
                  </button>
                </>
              )}
          </div>
          {usable && (
            <div className="ws-msg-actions" role="toolbar" aria-label="Thao tác tin nhắn">
              <IconButton
                ref={reactRef}
                label="Thả cảm xúc"
                icon="smile"
                size={16}
                data-testid="message-react"
                aria-expanded={picker}
                onClick={() => setPicker((p) => !p)}
              />
              {props.onReply && <IconButton label="Trả lời" icon="reply" size={16} data-testid="message-reply" onClick={props.onReply} />}
              {props.onOpenThread && threadable && (
                <IconButton label="Trả lời trong luồng" icon="chat" size={16} data-testid="message-thread" onClick={props.onOpenThread} />
              )}
              {props.onPin && threadable && (
                <IconButton
                  label={props.pinned ? 'Bỏ ghim' : 'Ghim'}
                  icon="pin"
                  size={16}
                  active={props.pinned}
                  data-testid="message-pin"
                  onClick={props.onPin}
                />
              )}
              {mine && props.onImportant && (
                <IconButton
                  label={m.important ? 'Bỏ đánh dấu quan trọng' : 'Đánh dấu quan trọng'}
                  icon="star"
                  size={16}
                  active={m.important}
                  data-testid="message-important"
                  onClick={props.onImportant}
                />
              )}
              {props.onForward && isText && <IconButton label="Chuyển tiếp" icon="forward" size={16} data-testid="message-forward" onClick={props.onForward} />}
              {mine && isText && <IconButton label="Sửa" icon="edit" size={16} onClick={props.onEdit} />}
              {mine && <IconButton label="Xóa" icon="trash" size={16} onClick={props.onDelete} />}
            </div>
          )}
          {picker && usable && <LazyReactionPicker m={m} me={props.me} trigger={reactRef} onReact={props.onReact} onClose={() => setPicker(false)} />}
        </div>
        {/* Danh sách cảm xúc nằm dưới khung tin (kiểu Zalo), chồng nhẹ lên mép dưới khung. */}
        {reactions.length > 0 && (
          <div className="ws-msg-reactions" data-testid="reactions">
            {reactions.map(([emoji, users]) => {
              const reacted = !!props.me && users.includes(props.me);
              return (
                <button
                  key={emoji}
                  type="button"
                  className={reacted ? 'ws-reaction mine' : 'ws-reaction'}
                  data-testid="reaction"
                  data-emoji={emoji}
                  title={users.map((u) => props.senderName(u) ?? '…').join(', ')}
                  aria-pressed={reacted}
                  onClick={() => props.onReact(emoji, reacted)}
                >
                  <span className="ws-reaction-emoji">{emoji}</span> {users.length}
                </button>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
